import { CollisionPriority } from "@dnd-kit/abstract";
import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { flatMap } from "lodash";
import React, { useEffect, useRef, useState } from "react";

type WithUuid = {
  uuid: string;
};

type ArrayKey<T> = {
  [K in keyof T]: T[K] extends WithUuid[] ? K : never;
}[keyof T];

type ItemType<C, K extends ArrayKey<C>> = Extract<C[K], WithUuid[]>[number];

type Props<C extends WithUuid, K extends ArrayKey<C>> = {
  collection: C[];
  itemKey: K;
  setCollection: (collection: C[]) => void;
  children: (collection: C[]) => React.ReactNode;
};

function DragNDrop<C extends WithUuid, K extends ArrayKey<C>>({
  collection,
  itemKey,
  setCollection,
  children,
}: Props<C, K>) {
  const packItems = (collection: C[]) =>
    collection.reduce((acc, group) => {
      acc[group.uuid] = (group[itemKey] as ItemType<C, K>[]).map(
        (item) => item.uuid
      );
      return acc;
    }, {} as Record<string, string[]>);
  const unpackItems = (
    items: Record<string, string[]>,
    columnOrder: string[],
    collection: C[]
  ) => {
    const allItems = flatMap(
      collection,
      (group) => group[itemKey] as ItemType<C, K>[]
    );

    return columnOrder
      .map((groupId) => {
        const group = collection.find((g) => g.uuid === groupId);
        if (!group) return null;

        return {
          ...group,
          [itemKey]: items[groupId]
            .map((itemId) => allItems.find((i) => i.uuid === itemId))
            .filter(Boolean) as ItemType<C, K>[],
        };
      })
      .filter(Boolean) as C[];
  };

  const [items, setItems] = useState(packItems(collection));
  const previousItems = useRef(items);
  const [columnOrder, setColumnOrder] = useState(() => Object.keys(items));
  /**
   * This useEffect is for changes other than drag and drop (rearranging) i.e new information
   * DradNDrop is only responsible for rearranging, and the collection can be modified outside of it with peace of mind
   */
  useEffect(() => {
    const sourceItemLength = collection.reduce(
      (acc, group) => [...acc, ...(group[itemKey] as ItemType<C, K>[])],
      [] as ItemType<C, K>[]
    ).length;
    const localItemLength = Object.values(items).reduce(
      (acc, itemIds) => [...acc, ...itemIds],
      [] as string[]
    ).length;

    const sourceGroupLength = collection.length;
    const localGroupLength = Object.keys(items).length;
    console.log(
      "lengths",
      sourceItemLength,
      localItemLength,
      sourceGroupLength,
      localGroupLength
    );

    if (
      sourceItemLength !== localItemLength ||
      sourceGroupLength !== localGroupLength
    ) {
      console.log("useEffect ran");

      const tempItems = packItems(collection);

      setItems(tempItems);
      previousItems.current = tempItems;
      setColumnOrder(Object.keys(tempItems));
    }
  }, [collection]);
  useEffect(() => {
    console.log("column order changed", columnOrder);

    setCollection(unpackItems(items, columnOrder, collection));
  }, [columnOrder]);

  console.log("items", items);
  console.log("columnOrder", columnOrder);

  return (
    <DragDropProvider
      onDragStart={() => {
        previousItems.current = items;
      }}
      onDragOver={(event) => {
        const { source } = event.operation;

        if (source?.type === "column") return;

        setItems((items) => move(items, event));
      }}
      onDragEnd={(event) => {
        const { source } = event.operation;

        if (event.canceled) {
          if (source?.type === "item") {
            setItems(previousItems.current);
          }

          return;
        }

        if (source?.type === "column") {
          setColumnOrder((columnOrder) => move(columnOrder, event));
        } else {
          // This is assuming the type is "item"
          // we are propagating this change to collections here because it changes throughout the drag
          // whereas columns will be propagated with a useEffect outside of the dnd context, because they only change at the end of the drag
          setCollection(unpackItems(items, columnOrder, collection));
        }
      }}
    >
      <>{children(unpackItems(items, columnOrder, collection))}</>
    </DragDropProvider>
  );
}

interface SortableColumnProps {
  id: string;
  index: number;
  children: (
    sortableColumnProps: ReturnType<typeof useSortable>
  ) => React.ReactNode;
}

export const SortableColumn: React.FC<SortableColumnProps> = ({
  id,
  index,
  children,
}) => {
  useEffect(() => {
    console.log("Mounted SortableColumn");
    return () => console.log("Unmounted SortableColumn");
  }, []);

  const sortableColumnProps = useSortable({
    id,
    index,
    type: "column",
    collisionPriority: CollisionPriority.Low,
    accept: ["item", "column"],
  });

  return <>{children(sortableColumnProps)}</>;
};

interface SortableItemProps {
  id: string;
  index: number;
  column: string;
  children: (
    sortableItemProps: ReturnType<typeof useSortable>
  ) => React.ReactNode;
}

export const SortableItem: React.FC<SortableItemProps> = ({
  id,
  index,
  column,
  children,
}) => {
  useEffect(() => {
    console.log("Mounted SortableItem");
    return () => console.log("Unmounted SortableItem");
  }, []);

  const sortableItemProps = useSortable({
    id,
    index,
    type: "item",
    accept: "item",
    group: column,
  });

  return <>{children(sortableItemProps)}</>;
};

export default DragNDrop;
